import { useLocale } from 'shared/library/i18n/use-locale';
import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  formatRelative,
  isSameDay,
  isSameWeek,
  parseISO,
  subDays,
} from 'date-fns';
import { da as daLocale } from 'date-fns/locale/da';
import { enUS } from 'date-fns/locale/en-US';
import { sv } from 'date-fns/locale/sv';
import { nb } from 'date-fns/locale/nb';
import { fi } from 'date-fns/locale/fi';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { formatCurrency as formatCurrencyValue } from 'shared/library/format-currency';

export const DATE_LOCALES = {
  da: daLocale,
  en: enUS,
  sv,
  no: nb,
  fi,
};

const parseDate = (date: string): Date => {
  if (!date.slice(0, 8).includes('-')) {
    date = date.slice(0, 8);
  }

  return parseISO(date.replace(' ', 'T') + 'Z');
};

export function useFormatter() {
  const { t } = useTranslation('library');
  const { languageCode, market, timezone } = useLocale();
  const fnsLocale = DATE_LOCALES[languageCode];

  function formatCurrency(
    price: number | undefined,
    currency?: string,
    maximumFractionDigits = 0,
  ): string {
    return formatCurrencyValue(price, currency ?? market.currency, maximumFractionDigits);
  }

  function formatDateAsTimeAgo(date: string): string {
    if (!date) {
      return '-';
    }

    const parsedDate = parseDate(date);
    const dateInTz = toZonedTime(parsedDate, timezone);
    const nowInTz = toZonedTime(new Date(), timezone);

    if (isSameDay(dateInTz, nowInTz)) {
      return `${t('use_formatter.today')} ${formatInTimeZone(parsedDate, timezone, 'HH:mm', { locale: fnsLocale })}`;
    }

    if (isSameDay(dateInTz, subDays(nowInTz, 1))) {
      return `${t('use_formatter.yesterday')} ${formatInTimeZone(parsedDate, timezone, 'HH:mm', { locale: fnsLocale })}`;
    }

    if (isSameWeek(dateInTz, nowInTz, { weekStartsOn: 1 })) {
      return formatInTimeZone(parsedDate, timezone, `EEEE HH:mm`, { locale: fnsLocale });
    }

    return formatInTimeZone(parsedDate, timezone, 'd MMM yyyy', { locale: fnsLocale });
  }

  function formatDate(date: string | null | undefined): string {
    if (!date) {
      return '-';
    }

    return formatRelative(
      toZonedTime(parseDate(date), timezone),
      toZonedTime(new Date(), timezone),
      {
        locale: fnsLocale,
      },
    );
  }

  function formatDateCustom(date: string | Date, outputFormat = 'do LLLL yyyy'): string {
    return formatInTimeZone(
      typeof date === 'string' ? parseDate(date) : date,
      timezone,
      outputFormat,
      {
        locale: fnsLocale,
      },
    );
  }

  function formatDateAsAge(temp: string, addSuffix = true): string {
    const now = new Date();
    const birth_date = parseDate(temp);

    const years = differenceInYears(now, birth_date);
    const months = differenceInMonths(now, birth_date);
    const weeks = differenceInWeeks(now, birth_date);
    const days = differenceInDays(now, birth_date);
    let age = '';

    if (days < 0) {
      return t('use_formatter.not_born_yet', { date: temp });
    } else if (years >= 2) {
      age = t('use_formatter.years', { count: years });
    } else if (weeks < 2) {
      age = t('use_formatter.days', { count: days });
    } else if (months <= 2) {
      age = t('use_formatter.weeks', { count: weeks });
    } else {
      age = t('use_formatter.months', { count: months });
    }

    if (addSuffix) {
      return t('use_formatter.age_old', { age });
    } else {
      return age;
    }
  }

  function formatDateTime(date: string | null): string {
    if (!date) {
      return '-';
    }

    return formatInTimeZone(parseDate(date), timezone, 'PPPp', { locale: fnsLocale });
  }

  return {
    formatCurrency,
    formatDate,
    formatDateCustom,
    formatDateAsTimeAgo,
    formatDateAsAge,
    formatDateTime,
  };
}
